// services/followup.ts — API client for Follow-Up Hub

// ---------- Types ----------

export interface FollowUpContract {
  'Contract Title': string;
  'Contract Status': string;
  'Contract Delivered': string;
}

export interface FollowUpRecord {
  GROUP_ID: string;
  GROUP_NAME: string;
  CLIENTPARTNER: string;
  CLIENTMANAGER: string;
  CLIENTOFFICE: string;
  CLIENT_CONTACT_NAME: string;
  PARTY_EMAIL: string;
  PHONENUMBERS: Record<string, string>;
  OPEN_CONTRACTS: FollowUpContract[];
  CY_WIP: number;
  PRIMARY_KEY: string;
}

export interface FollowUpState {
  status: string;
  notes: string;
  updatedAt: string;
  updatedBy: { user: string; fullname: string };
}

export interface Lock {
  ts: string;
  user: string;
  fullname: string;
}

export interface AuditEntry {
  ts: string;
  user: string;
  fullname: string;
  status: string;
  notes: string;
}

export interface Employee {
  fullname: string;
  user: string;
}

export interface BootstrapData {
  records: FollowUpRecord[];
  states: Record<string, FollowUpState>;
  locks: Record<string, Lock>;
}

// ---------- Field parsers ----------

/**
 * PARTY_EMAIL arrives as a free-text blob that can hold several addresses in
 * any separator style, so pull them out by pattern and de-dupe.
 */
export function extractEmails(raw: string): string[] {
  const re = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
  return [...new Set(raw.match(re) || [])];
}

// ---------- Helpers ----------

async function jsonOrThrow(res: Response, label: string): Promise<any> {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${label}: ${res.status} ${res.statusText} ${text.slice(0, 200)}`);
  }
  return res.json();
}

function get(url: string, label: string) {
  return fetch(url, { credentials: 'include', cache: 'no-store' }).then((r) =>
    jsonOrThrow(r, label),
  );
}

function post(url: string, body: object, label: string) {
  return fetch(url, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => jsonOrThrow(r, label));
}

// ---------- API functions ----------

export function fetchBootstrap(): Promise<BootstrapData> {
  return get('/api/followup-bootstrap', 'bootstrap');
}

export function fetchEmployees(): Promise<Employee[]> {
  return get('/api/followup-employee', 'employee');
}

export function fetchAudit(key: string): Promise<AuditEntry[]> {
  return get(`/api/followup-audit?key=${encodeURIComponent(key)}`, 'audit').then((data) => {
    // Normalize: LA may return { audits: [...] } or [...] or { entries: [...] }
    const arr = Array.isArray(data)
      ? data
      : Array.isArray(data?.audits)
        ? data.audits
        : Array.isArray(data?.entries)
          ? data.entries
          : [];
    return arr
      .filter((e: any) => e && (e.ts || e.timestamp))
      .map((e: any) => ({
        ts: String(e.ts || e.timestamp || ''),
        user: String(e.user || ''),
        fullname: String(e.fullname || ''),
        status: String(e.status || ''),
        notes: String(e.notes || ''),
      }))
      .sort((a: AuditEntry, b: AuditEntry) => b.ts.localeCompare(a.ts));
  });
}

export function acquireLock(
  key: string,
  ttlMinutes = 10,
): Promise<{ ok: boolean; lock?: Lock; locks?: Record<string, Lock> }> {
  return post('/api/followup-lock-acquire', { key, ttlMinutes }, 'lockAcquire');
}

export function releaseLock(key: string): Promise<{ ok: boolean }> {
  return post('/api/followup-lock-release', { key }, 'lockRelease');
}

export function fetchLocks(): Promise<Record<string, Lock>> {
  return get('/api/followup-locks', 'locks').then((data) => {
    return data?.locks && typeof data.locks === 'object' ? data.locks : data ?? {};
  });
}

export function fetchStates(): Promise<Record<string, FollowUpState>> {
  return get('/api/followup-states', 'states').then((data) => {
    return data?.states && typeof data.states === 'object' ? data.states : data ?? {};
  });
}

export function saveFollowUp(
  key: string,
  status: string,
  notes: string,
): Promise<{ ok: boolean }> {
  return post('/api/followup-save', { key, status, notes }, 'save');
}
